import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useImperativeHandle, useRef } from "react";
import { isSafariBrowser } from "~/utils/BrowserUtils";
import { leftMouseEvent } from "~/utils/ClickUtils";
const _DrawingCanvas = /*#__PURE__*/ forwardRef((param, ref)=>{
    let { onMouseDown, onSizeChanged, containerHeight, containerWidth, containerTop, containerLeft, onMouseMove, onMouseUp, hasCustomCursor } = param;
    const canvasRef = useRef(null);
    useImperativeHandle(ref, ()=>({
            getCanvas: ()=>canvasRef === null || canvasRef === void 0 ? void 0 : canvasRef.current,
            clearCanvas: ()=>{
                const canvas = canvasRef.current;
                if (canvas) {
                    const ctx = canvas.getContext("2d");
                    if (!!ctx) {
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                }
            },
            getMouseCoords: (param)=>{
                let { event, lineWidth = 0 } = param;
                const height = containerHeight !== null && containerHeight !== void 0 ? containerHeight : 0;
                const width = containerWidth !== null && containerWidth !== void 0 ? containerWidth : 0;
                // cursor offset does not work on Safari, so add 18 (offsetY) for correct position
                const clientY = isSafariBrowser && hasCustomCursor ? event.clientY + 18 : event.clientY;
                const eventX = Math.min(event.clientX, containerLeft + width - lineWidth);
                const eventY = Math.min(clientY, containerTop + height - lineWidth);
                return {
                    x: Math.max(lineWidth, eventX - containerLeft + lineWidth),
                    y: Math.max(lineWidth, eventY - containerTop + lineWidth)
                };
            }
        }));
    // This desired triggers for this effect are the containerHeight or containerWidth changing.
    // We want to update the canvas elements width and height so that shapes are drawn correctly.
    // We also want to re-draw the active box, if there is one, based on the new dimensions.
    useEffect(()=>{
        // Set the canvas dimensions when the parent size changes.
        if (!!canvasRef.current && !!containerHeight && !!containerWidth) {
            const ctx = canvasRef.current.getContext("2d");
            if (!!ctx) {
                var _window_devicePixelRatio;
                // set bigger size for retina display to avoid blurred lines
                // for bigger screens, even if devicePixelRatio is 1, setting 2 gives better lines
                const devicePixelRatio = Math.max(2, "object" === "undefined" ? 2 : (_window_devicePixelRatio = window.devicePixelRatio) !== null && _window_devicePixelRatio !== void 0 ? _window_devicePixelRatio : 2);
                ctx.canvas.height = devicePixelRatio * containerHeight;
                ctx.canvas.width = devicePixelRatio * containerWidth;
                ctx.canvas.style.width = "".concat(containerWidth, "px");
                ctx.canvas.style.height = "".concat(containerHeight, "px");
                ctx.scale(devicePixelRatio, devicePixelRatio);
            }
        }
    }, [
        containerHeight,
        containerWidth
    ]);
    useEffect(()=>{
        onSizeChanged === null || onSizeChanged === void 0 ? void 0 : onSizeChanged();
    // omitted onSizeChanged - we want to call it when width or height changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        containerWidth,
        containerHeight
    ]);
    return /*#__PURE__*/ _jsx("canvas", {
        "data-testid": "DRAWING_CANVAS",
        ref: canvasRef,
        onMouseDown: onMouseDown ? leftMouseEvent(onMouseDown) : undefined,
        onMouseMove: onMouseMove,
        onMouseUp: onMouseUp ? leftMouseEvent(onMouseUp) : undefined
    });
});
_DrawingCanvas.displayName = "DrawingCanvas";
export const DrawingCanvas = /*#__PURE__*/ memo(_DrawingCanvas);
DrawingCanvas.displayName = "DrawingCanvas";
